import { memo, useEffect, useMemo, useCallback } from 'react';
import { Box, CssBaseline, Grid, ListItemText, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { showMessage } from 'store/message/messageSlice';
import { IContent } from 'interface/project/projectInfo';
import { useProject } from 'hooks/useProject';
import ManagementTable from 'components/project/management/ManagementTable';

function ProjectManagement(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getCommCodes } = useProject();
  const location = useLocation();
  const typeValue = location.state?.codeValue || 'notfound';
  const detailInfo = (location.state?.rowInfo as IContent) || {};
  const fromPath = location.state?.from || '/admin/project/overview';

  useEffect(() => {
    if (!['ID', 'DC', 'ME'].includes(typeValue)) {
      dispatch(showMessage({ message: '부적절한 경로로 접근하였습니다. 다시 시도해주세요.', variant: 'warning' }));
      navigate('/admin/project/create');
    }
  }, [dispatch, navigate, typeValue]);

  const { data: commCodesData } = getCommCodes();
  const handleFindInfoByCodeValue = useCallback(
    (codeValue: string): string => {
      return commCodesData?.find(code => code.codeValue === codeValue)?.codeName || '-';
    },
    [commCodesData],
  );
  const handleAccountsListByComma = useMemo(() => {
    if (!detailInfo?.projectAdmins || detailInfo?.projectAdmins.length === 0) {
      return '';
    }
    return detailInfo?.projectAdmins?.map(obj => obj.name)?.join(', ');
  }, [detailInfo?.projectAdmins]);

  const data = [
    {
      xs: 6,
      sm: 3,
      md: 2,
      primary: '프로젝트 유형',
      secondary: `${handleFindInfoByCodeValue(detailInfo?.projectType)}`,
    },
    { xs: 6, sm: 3, md: 2, primary: '프로젝트 이름', secondary: `${detailInfo?.projectName}` },
    { xs: 6, sm: 3, md: 2, primary: '프로젝트 기업명', secondary: `${detailInfo?.compName}` },
    {
      xs: 6,
      sm: 3,
      md: 2,
      primary: '프로젝트 기간',
      secondary: `${detailInfo.projectStartAt} ~ ${detailInfo.projectEndAt}`,
    },
    {
      xs: 6,
      sm: 3,
      md: 3,
      primary: '프로젝트 담당자',
      secondary: `${handleAccountsListByComma}`,
      secondarySx: {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        display: 'block',
      },
    },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Grid container>
        <Stack direction="column" sx={{ flexGrow: 1 }}>
          <Grid container>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={3}>
                {data.map(obj => (
                  <Grid key={obj.primary} item xs={obj.xs} sm={obj.sm} md={obj.md} justifyContent="flex-start" sx={{ display: 'flex' }}>
                    <ListItemText
                      primary={obj.primary}
                      secondary={obj.secondary}
                      primaryTypographyProps={{ typography: 's16w800' }}
                      secondaryTypographyProps={{ typography: 's14w500', ...obj?.secondarySx }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Stack>
        <ManagementTable
          projectId={detailInfo?.id || ''}
          projectState={detailInfo?.projectState || ''}
          from={fromPath}
          type="admin"
        />
      </Grid>
    </Box>
  );
}

export default memo(ProjectManagement);
